import { apiBlob } from "../api/client";

/**
 * THE BADGE AS A PDF — the one the organiser drew, rendered by the server.
 *
 *   POST /domains/:d/events/:e/users/:u/badge/render        apix
 *
 * ⚠️ ON APIX, NOT ON API-V1. The two addresses look alike and aiming at the wrong one
 * answers "Oops, cette route n'existe pas" — the trap `api/client.ts` exists to make
 * impossible to fall into silently: the API is NAMED, never guessed.
 *
 * ⚠️ THE SERVER PICKS THE TEMPLATE, AND IT PICKS IT FOR THIS HOLDER: their language and
 * their typology, through the same cascade as `GET /settings/fo/me`
 * (`api/src/routes/badgeTemplates/renderUser`). Nothing to pass it, and above all no
 * `templateId` guessed here — V1 learned that one: a front that preselects a template
 * prints the wrong badge.
 *
 * ⚠️ NO TEMPLATE CONFIGURED IS AN ERROR, NOT AN EMPTY PDF. The route refuses rather than
 * returning a blank page, and its message says so. The screen repeats it verbatim: the
 * organiser reading it is precisely the person who can fix it.
 *
 * ═══ UN BADGE, OU PLUSIEURS DANS LE MÊME DOCUMENT ═══
 *
 * ⚠️ LE LOT EST UNE CAPACITÉ DE LA ROUTE, PAS UNE BOUCLE ICI. Elle accepte un `userIds`
 * dans son corps — « impression par lot », c'est son mot — et rend UN document. Émettre
 * une requête par personne aurait produit trois téléchargements que le navigateur
 * bloque après le premier, et surtout trois PDF à imprimer un par un alors qu'on veut
 * une feuille à découper.
 *
 * ⚠️ ET UN SEUL MODÈLE PAR RENDU, CE QUI EST LA RÈGLE DU SERVEUR : il résout le modèle
 * pour CHAQUE porteur et refuse si leurs langues ou leurs typologies en désignent des
 * différents, plutôt que d'en choisir un au hasard. Rien à décider ici — surtout pas un
 * `templateId` deviné, la faute que la V1 a déjà payée.
 */
export async function downloadBadge(
  holder: { domainId: number; eventId: number; userId: number },
  fileName: string,
  /**
   * Les autres porteurs à mettre dans le MÊME document. Vide ou absent : le seul
   * `userId` de l'adresse, comme avant.
   */
  also?: ReadonlyArray<number>,
  signal?: AbortSignal,
): Promise<void> {
  const all = [holder.userId, ...(also ?? [])];
  const blob = await apiBlob(
    `/domains/${holder.domainId}/events/${holder.eventId}/users/${holder.userId}/badge/render`,
    {
      method: "POST",
      /* ⚠️ ON N'ENVOIE `userIds` QUE S'IL Y EN A PLUSIEURS. Seul, il ne dirait rien de
         plus que l'adresse — et un corps qui répète le chemin est une seconde source
         de vérité pour la même chose. */
      body: all.length > 1 ? { userIds: all } : {},
      api: "apix",
      ...(signal ? { signal } : {}),
    },
  );

  const url = URL.createObjectURL(blob);
  try {
    /**
     * ⚠️ AN `<a download>`, AND THERE IS NO OTHER WAY TO NAME THIS FILE. The PDF comes
     * back *inline*; a blob URL carries no name, and the server's `Content-Disposition`
     * does not cross the boundary between the two domains. Opening the blob in a tab —
     * what V1 does — delivers a file named after a string of hexadecimal. See
     * `badgeFileName`.
     */
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    /**
     * ⚠️ NOT STRAIGHT AWAY. The download starts asynchronously: revoking the URL right
     * after the click cancels it in some browsers, and the user sees a failure with no
     * cause. One turn of the loop is enough to let the request leave.
     */
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}
